import { Component, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { provideTranslocoScope } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { Observable, of, switchMap, tap } from 'rxjs';
import { MARKETPLACE_TRANSLOCO_SCOPE } from '../../marketplace-transloco-scope';
import { CountryService } from '../country.service';
import { CountryState } from '../country.state';

@UntilDestroy()
@Component({
  selector: 'yat-country-select',
  templateUrl: './country-select.component.html',
  styleUrls: ['./country-select.component.scss'],
  providers: [
    provideTranslocoScope(MARKETPLACE_TRANSLOCO_SCOPE),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountrySelectComponent),
      multi: true
    }
  ]
})
export class CountrySelectComponent implements OnInit, ControlValueAccessor {
  constructor(private readonly countryService: CountryService) {}

  @Select(CountryState.countries)
  countries$: Observable<string[]>;

  sortedCountries: string[] = [];
  value: string = '';
  isFocused: boolean = false;

  private onChange: (value: string) => void;
  private onTouched: () => void;

  get hasValue(): boolean {
    return this.value !== null && this.value !== '';
  }

  ngOnInit(): void {
    this.countries$
      .pipe(
        switchMap(countries => {
          if (!countries || countries.length === 0) {
            return of([]);
          }
          return this.countryService.getSortedCountries(countries);
        }),
        tap(sortedCountries => (this.sortedCountries = sortedCountries)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  onValueChange(value?: string): void {
    const newValue = value;
    if (newValue === this.value) {
      return;
    }

    this.value = newValue ?? '';
    this.onChange(newValue ?? '');
    this.onTouched();
  }

  onFocus(): void {
    this.isFocused = true;
  }

  onBlur(): void {
    this.isFocused = false;
  }
}
